// @ts-nocheck
import React, { useState, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import BasicInfoTab from "@/components/admin-dashboard/event-form/BasicInfoTab";
import VenueLocationTab from "@/components/admin-dashboard/event-form/VenueLocationTab";
import CategoriesRegistrationTab from "@/components/admin-dashboard/event-form/CategoriesRegistrationTab";
import RegistrationPricingTab from "@/components/admin-dashboard/event-form/RegistrationPricingTab";
import ExtrasTab from "@/components/admin-dashboard/event-form/ExtrasTab";
import DetailsTab from "@/components/admin-dashboard/event-form/DetailsTab";
import SponsorsTab from "@/components/admin-dashboard/event-form/SponsorsTab";

import FAQsTab from "@/components/admin-dashboard/event-form/FAQsTab";

import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Save } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useOrganization } from "@/hooks/useOrganization";
import { useBeforeUnload } from "@/hooks/useBeforeUnload";

export interface EventFormData {
  id?: string;
  name: string;
  description: string;
  start_date: string;
  end_date: string;
  registration_open_date: string;
  registration_close_date: string;
  gender: string;
  sport_id: string;
  event_type_id: string;
  event_category_ids: string[];
  accept_online_registrations: boolean;
  allow_checkin: boolean;
  registration_type: 'individual' | 'team';
  allow_coupons: boolean;
  discount_policy_id: string;
  external_registration_url: string;
  venue_name: string;
  venue_address: string;
  venue_city: string;
  venue_state: string;
  venue_zip: string;
  venue_id: string;
  main_venue_id?: string;
  additional_venue_ids: string[];
  registration_fee: number;
  max_participants: number;
  roster_deadline: string;
  gallery_images: string[];
  event_format: string;
  checkin_short_description?: string | null;
  checkin_instructions: string;
  rules_short_description?: string | null;
  rules: string;
  schedule_info: string;
  pricing_notes: string;
  age_groups_description: string;
  hotels_short_description?: string | null;
  hotel_booking_url: string;
  promo_video_url: string;
  live_stream_short_description?: string | null;
  live_streaming_description: string;
  live_streaming_url: string;
  enable_media_credentials: boolean;
  media_credentials_short_description?: string | null;
  media_credentials_description: string;
  media_credentials_form_id: string;
  media_credentials_notification_email: string;
  media_credentials_price: number;
  media_credentials_coupon_code: string;
  media_credentials_coupon_discount_type: string;
  media_credentials_coupon_discount_value: number;
  coach_notification_email: string;
  coach_coupon_code: string;
  enable_custom_form: boolean;
  custom_form_description: string;
  custom_form_id: string;
  display_format: string;
  rules_instructions: string;
  selected_sponsor_ids: string[];
  new_sponsor: {
    name: string;
    website_url: string;
    logo_file: File | null;
  };
  status: string;
  thumbnail_url: string | null;
  cover_photo_url: string | null;
  background_image_url: string | null;
  accept_special_requests: boolean;
  special_request_instructions: string;
  special_request_fee: number;
  pricing_display_text: string;
  tickets_short_description?: string | null;
  ticket_display_text: string;
  coaches_external_url: string;
  enable_coach_info: boolean;
  coaches_short_description?: string | null;
  coach_info_description: string;
  coach_info_form_id: string;
  more_info: string;
  pricing_tiers: PricingTier[];
  age_groups: AgeGroup[];
  spectator_tickets: SpectatorTicket[];
  event_extras: EventExtra[];
  notification_recipients: NotificationRecipient[];
  // Packet configuration
  packet_enabled?: boolean;
  packet_price?: number;
  default_packet_template_id?: string;
  packet_default_age_groups?: string[];
  packet_include_all_age_groups?: boolean;
  purchasable_packet_url?: string;
  // Integrations
  google_sheets_webhook_url?: string;
  google_sheets_tab_name?: string;
  google_sheets_column_mapping?: Record<string, string>;
  media_credentials_sheets_id?: string;
  media_credentials_sheets_tab_name?: string;
  coach_attendance_google_sheet_url?: string;
  coach_attendance_sheets_tab_name?: string;
}

export interface PricingTier {
  tier_name: string;
  price: string | number; // Allow both string and number
  registration_form_id: string;
  is_active: boolean;
}

export interface AgeGroup {
  name: string;
  registration_fee: string;
  registration_form_id: string;
  is_active?: boolean;
  accept_special_requests?: boolean;
  special_request_fee?: string;
}



export interface SpectatorTicket {
  id: string;
  name: string;
  price: string | number; // Allow both string and number
  is_active: boolean;
  thumbnail_url: string;
  qr_code_value: string;
}

export interface EventExtra {
  id: string;
  name: string;
  price: string | number; // Allow both string and number
  description: string;
  is_active: boolean;
  thumbnail_url: string;
}

export interface NotificationRecipient {
  id?: string;
  email: string;
  notification_type?: string;
}

interface CreateEventFormProps {
  onEventCreated?: () => void;
}

const CreateEventForm: React.FC<CreateEventFormProps> = ({ onEventCreated }) => {
  const { organization } = useOrganization();
  const [activeTab, setActiveTab] = useState<string>("basic-info");
  const [loading, setLoading] = useState(false);
  const [tabSaveLoading, setTabSaveLoading] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  const STORAGE_KEY = 'event-creation-draft';
  
  const [formData, setFormData] = useState<EventFormData>({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    registration_open_date: '',
    registration_close_date: '',
    gender: '',
    sport_id: '',
    event_type_id: '',
    event_category_ids: [],
    accept_online_registrations: false,
    allow_checkin: false,
    registration_type: 'individual',
    allow_coupons: false,
    discount_policy_id: '',
    external_registration_url: '',
    venue_name: '',
    venue_address: '',
    venue_city: '',
    venue_state: '',
    venue_zip: '',
    venue_id: '',
    main_venue_id: '',
    additional_venue_ids: [],
    registration_fee: 0,
    max_participants: 0,
    roster_deadline: '',
    gallery_images: [],
    event_format: '',
    checkin_short_description: '',
    checkin_instructions: '',
    rules_short_description: '',
    rules: '',
    schedule_info: '',
    pricing_notes: '',
    age_groups_description: '',
    hotels_short_description: '',
    hotel_booking_url: '',
    promo_video_url: '',
    live_stream_short_description: '',
    live_streaming_description: '',
    live_streaming_url: '',
    enable_media_credentials: false,
    media_credentials_short_description: '',
    media_credentials_description: '',
    media_credentials_form_id: '',
    media_credentials_notification_email: '',
    media_credentials_price: 0,
    media_credentials_coupon_code: '',
    media_credentials_coupon_discount_type: 'percentage',
    media_credentials_coupon_discount_value: 100,
    coach_notification_email: '',
    coach_coupon_code: '',
    enable_custom_form: false,
    custom_form_description: '',
    custom_form_id: '',
    display_format: '',
    rules_instructions: '',
    selected_sponsor_ids: [],
    new_sponsor: {
      name: '',
      website_url: '',
      logo_file: null
    },
    status: '',
    thumbnail_url: null,
    cover_photo_url: null,
    background_image_url: null,
    accept_special_requests: false,
    special_request_instructions: '',
    special_request_fee: 0,
    pricing_display_text: '',
    tickets_short_description: '',
    ticket_display_text: '',
    coaches_external_url: '',
    enable_coach_info: false,
    coaches_short_description: '',
    coach_info_description: '',
    coach_info_form_id: '',
    more_info: '',
    pricing_tiers: [],
    age_groups: [],
    spectator_tickets: [],
    event_extras: [],
    notification_recipients: [],
    // Packet configuration defaults
    packet_enabled: false,
    packet_price: 0,
    default_packet_template_id: '',
    packet_default_age_groups: [],
    packet_include_all_age_groups: true,
    purchasable_packet_url: ''
  });

  // Load saved form data on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsedData = JSON.parse(saved);
        setFormData(parsedData);
        setHasUnsavedChanges(true);
        toast.info('Draft restored from your last session');
      }
    } catch (error) {
      console.warn('Failed to load saved form data:', error);
    }
  }, []);

  // Save form data to localStorage whenever it changes
  useEffect(() => {
    // Don't save the initial empty state
    if (formData.name || formData.description) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
        setHasUnsavedChanges(true);
      } catch (error) {
        console.warn('Failed to save form data:', error);
      }
    }
  }, [formData]);

  // Warn user before leaving page with unsaved changes
  useBeforeUnload({ 
    enabled: hasUnsavedChanges && !loading,
    message: 'You have unsaved event data. Are you sure you want to leave?'
  });

  const updateFormData = (data: Partial<EventFormData>) => {
    setFormData({ ...formData, ...data });
  };

  const clearSavedData = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.warn('Failed to clear saved data:', error);
    }
  };

  const handleSaveTab = async () => {
    setTabSaveLoading(true);
    try {
      // Save current tab data to local state (already handled by updateFormData)
      toast.success('Tab info saved successfully!');
    } catch (error) {
      console.error('Error saving tab data:', error);
      toast.error('Failed to save tab data');
    } finally {
      setTabSaveLoading(false);
    }
  };

  const handleFinalSubmit = async () => {
    try {
      setLoading(true);
      
      // Validate required fields
      if (!formData.name || !formData.start_date || !formData.event_type_id) {
        toast.error('Please fill in all required fields (Event Title, Start Date, Event Type)');
        return;
      }

      console.log('🚀 Submitting event form data:', formData);

      // Helper function to clean UUID fields
      const cleanUUID = (value: string | undefined | null): string | null => {
        if (!value || value.trim() === '') return null;
        return value;
      };

      // Helper function to clean date fields
      const cleanDate = (value: string | undefined | null): string | null => {
        if (!value || value.trim() === '') return null;
        return value;
      };

      // Create the event data object with only fields that exist in the events table
      const eventData = {
        name: formData.name,
        description: formData.description,
        start_date: cleanDate(formData.start_date),
        end_date: cleanDate(formData.end_date),
        event_type_id: cleanUUID(formData.event_type_id),
        registration_type: formData.registration_type,
        organization_id: organization.id, // Auto-assign organization based on current context
        additional_venue_ids: formData.additional_venue_ids || [],
        is_active: true,
        accept_online_registrations: formData.accept_online_registrations,
        external_registration_url: formData.external_registration_url || null,
        discount_policy_id: cleanUUID(formData.discount_policy_id),
        roster_deadline: formData.registration_type === 'team' ? cleanDate(formData.roster_deadline) : null,
        promo_video_url: formData.promo_video_url || null,
        gallery_images: formData.gallery_images || [],
        registration_open_date: cleanDate(formData.registration_open_date),
        registration_close_date: cleanDate(formData.registration_close_date),
        event_category_ids: formData.event_category_ids || [],
        registration_alert_emails: formData.notification_recipients?.map(r => r.email) || [],
        payment_notification_emails: formData.notification_recipients?.map(r => r.email) || [],
        roster_upload_emails: formData.notification_recipients?.map(r => r.email) || [],
        media_credentials_description: formData.media_credentials_description || null,
        media_credentials_form_id: cleanUUID(formData.media_credentials_form_id),
        media_credentials_notification_email: formData.media_credentials_notification_email || null,
        coach_notification_email: formData.coach_notification_email || null,
        live_streaming_description: formData.live_streaming_description || null,
        live_streaming_url: formData.live_streaming_url || null,
        allow_checkin: formData.allow_checkin,
        created_by: null, // Will be set by database trigger or backend
        status: 'draft',
        allow_coupons: formData.allow_coupons,
        venue_name: formData.venue_name || null,
        venue_city: formData.venue_city || null,
        venue_state: formData.venue_state || null,
        main_venue_id: cleanUUID(formData.main_venue_id || formData.venue_id),
        checkin_instructions: formData.checkin_instructions || null,
        checkin_short_description: formData.checkin_short_description || null,
        display_format: formData.display_format || null,
        enable_media_credentials: formData.enable_media_credentials || false,
        media_credentials_short_description: formData.media_credentials_short_description || null,
        media_credentials_price: formData.media_credentials_price || 0,
        event_format: formData.event_format || null,
        hotel_booking_url: formData.hotel_booking_url || null,
        hotels_short_description: formData.hotels_short_description || null,
        pricing_notes: formData.pricing_notes || null,
        rules: formData.rules || null,
        rules_short_description: formData.rules_short_description || null,
        schedule_info: formData.schedule_info || null,
        age_groups_description: formData.age_groups_description || null,
        selected_sponsor_ids: formData.selected_sponsor_ids || [],
        is_archived: false,
        deleted_at: null,
        accept_special_requests: formData.accept_special_requests || false,
        special_request_instructions: formData.accept_special_requests ? formData.special_request_instructions : null,
        special_request_fee: formData.accept_special_requests ? formData.special_request_fee : null,
        thumbnail_url: formData.thumbnail_url || null,
        cover_photo_url: formData.cover_photo_url || null,
        background_image_url: formData.background_image_url || null,
        gender: formData.gender || null,
        sport_id: cleanUUID(formData.sport_id),
        enable_custom_form: formData.enable_custom_form || false,
        custom_form_description: formData.custom_form_description || null,
        custom_form_id: cleanUUID(formData.custom_form_id),
        purchasable_packet_url: formData.purchasable_packet_url || null,
        enable_coach_info: formData.enable_coach_info || false,
        coaches_short_description: formData.coaches_short_description || null,
        coach_info_description: formData.coach_info_description || null,
        coach_info_form_id: cleanUUID(formData.coach_info_form_id),
        tickets_short_description: formData.tickets_short_description || null,
        ticket_display_text: formData.ticket_display_text || null,
        live_stream_short_description: formData.live_stream_short_description || null,
        coaches_external_url: formData.coaches_external_url || null,
        more_info: formData.more_info || null,
        // Packet configuration
        packet_enabled: formData.packet_enabled || false,
        packet_price: formData.packet_price || 0,
        default_packet_template_id: cleanUUID(formData.default_packet_template_id),
        packet_default_age_groups: formData.packet_default_age_groups || [],
        packet_include_all_age_groups: formData.packet_include_all_age_groups ?? true
      };

      console.log('📝 Event data to insert:', eventData);

      // Insert the event
      const { data: eventResult, error: eventError } = await supabase
        .from('events')
        .insert([eventData])
        .select('*');

      if (eventError) {
        console.error('❌ Error creating event:', eventError);
        toast.error(`Failed to create event: ${eventError.message}`);
        return;
      }

      if (!eventResult || eventResult.length === 0) {
        console.error('❌ No event data returned from insert');
        toast.error('Failed to create event: No data returned');
        return;
      }

      const createdEvent = eventResult[0];
      console.log('✅ Event created successfully:', createdEvent);

      // Handle event categories - insert into event_category_mappings
      if (formData.event_category_ids && formData.event_category_ids.length > 0) {
        const categoryMappings = formData.event_category_ids.map(categoryId => ({
          event_id: createdEvent.id,
          event_category_id: categoryId
        }));

        const { error: categoriesError } = await supabase
          .from('event_category_mappings')
          .insert(categoryMappings);

        if (categoriesError) {
          console.error('❌ Error creating event category mappings:', categoriesError);
          toast.error(`Failed to create event categories: ${categoriesError.message}`);
          return;
        }

        console.log('✅ Event category mappings created successfully');
      }

      // Pricing tiers now managed via EventPricingConfig - see /admin/events/:id/pricing

      // Age groups now managed via EventPricingConfig - see /admin/events/:id/pricing


      // Spectator tickets now managed via EventPricingConfig - see /admin/events/:id/pricing

      // Event extras now managed via EventPricingConfig - see /admin/events/:id/pricing

      toast.success('Event created successfully!');
      
      // Clear saved draft after successful submission
      clearSavedData();
      
      // Call the callback to handle navigation
      if (onEventCreated) {
        onEventCreated();
      }

    } catch (error) {
      console.error('💥 Unexpected error:', error);
      toast.error('An unexpected error occurred while creating the event');
    } finally {
      setLoading(false);
    }
  };

  const isLastTab = activeTab === 'faqs';

  return (
    <div className="space-y-6">
      <Tabs defaultValue="basic-info" className="w-full">
        <TabsList className="grid w-full grid-cols-8 lg:grid-cols-8">
          <TabsTrigger value="basic-info">Basic Info</TabsTrigger>
          <TabsTrigger value="venues">Venues</TabsTrigger>
          <TabsTrigger value="registration">Registration</TabsTrigger>
          <TabsTrigger value="extras">Extras</TabsTrigger>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="sponsors">Sponsors</TabsTrigger>
          
          <TabsTrigger value="faqs">FAQs</TabsTrigger>
        </TabsList>

        <TabsContent value="basic-info">
          <BasicInfoTab formData={formData} updateFormData={updateFormData} />
        </TabsContent>

        <TabsContent value="venues">
          <VenueLocationTab formData={formData} updateFormData={updateFormData} />
        </TabsContent>

        <TabsContent value="registration">
          <RegistrationPricingTab formData={formData} updateFormData={updateFormData} />
        </TabsContent>

        <TabsContent value="extras">
          <ExtrasTab formData={formData} updateFormData={updateFormData} />
        </TabsContent>

        <TabsContent value="details">
          <DetailsTab formData={formData} updateFormData={updateFormData} />
        </TabsContent>

        <TabsContent value="sponsors">
          <SponsorsTab formData={formData} updateFormData={updateFormData} />
        </TabsContent>



        <TabsContent value="faqs">
          <FAQsTab eventId={formData.id} />
        </TabsContent>
      </Tabs>

      <div className="mt-4 flex justify-end space-x-2">
        <Button 
          variant="outline" 
          disabled={tabSaveLoading} 
          onClick={handleSaveTab}
        >
          <Save className="h-4 w-4 mr-2" />
          {tabSaveLoading ? "Saving..." : "Save This Info"}
        </Button>
        <Button 
          disabled={loading} 
          onClick={handleFinalSubmit}
          className="bg-primary text-primary-foreground"
        >
          {loading ? "Creating Event..." : "Create Event"}
        </Button>
      </div>
    </div>
  );
};

export default CreateEventForm;